import { useCallback, useEffect, useState } from 'react'

import type { NotesNotificationPreference } from '@disco/api/server'
import api from '@disco/api/server'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'

interface UseNotesNotificationPreference {
  preference: NotesNotificationPreference | null
  loading: boolean
  saving: boolean
  save: (next: NotesNotificationPreference) => Promise<void>
}

export const useNotesNotificationPreference =
  (): UseNotesNotificationPreference => {
    const [preference, setPreference] =
      useState<NotesNotificationPreference | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
      let active = true
      api
        .getNotesNotificationPreference()
        .then(result => {
          if (active) setPreference(result)
        })
        .catch(error => {
          pushUINotification({
            message: parseServerError(error?.body),
            level: 'error',
          })
        })
        .finally(() => {
          if (active) setLoading(false)
        })
      return () => {
        active = false
      }
    }, [])

    const save = useCallback(async (next: NotesNotificationPreference) => {
      setSaving(true)
      try {
        const result = await api.updateNotesNotificationPreference(next)
        setPreference(result)
        pushUINotification({
          message: 'Notification preferences saved.',
          level: 'success',
        })
      } catch (error) {
        pushUINotification({
          message: parseServerError(error?.body),
          level: 'error',
        })
      } finally {
        setSaving(false)
      }
    }, [])

    return { preference, loading, saving, save }
  }
